import React from 'react';
import {
    CheckSquare,
    User,
    Calendar,
    TrendingDown,
    ChevronDown,
    ChevronRight,
    Edit3,
} from 'lucide-react';
import clsx from 'clsx';
import { ActionWithContext } from '../../types';

interface ActionTrackerListProps {
    actions: ActionWithContext[];
    isDark: boolean;
    loading: boolean;
    allActionsCount: number;
    expandedItems: Set<string>;
    toggleExpand: (id: string) => void;
    onEdit: () => void;
    hasPermission: boolean;
    searchTerm?: string;
}

const ActionTrackerList: React.FC<ActionTrackerListProps> = ({
    actions,
    isDark,
    loading,
    allActionsCount,
    expandedItems,
    toggleExpand,
    onEdit,
    hasPermission,
}) => {
    // Utilities
    const formatDate = (dateString?: string) => {
        if (!dateString) return '—';
        const date = new Date(dateString + 'T00:00:00');
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const isOverdue = (action: ActionWithContext) => {
        const today = new Date().toISOString().split('T')[0];
        return (action.status === 'open' || action.status === 'in-progress') && action.targetDate < today;
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'open': return 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300';
            case 'in-progress': return 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300';
            case 'completed': return 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300';
            case 'cancelled': return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    if (loading) {
        return (
            <div className={clsx('rounded-xl border overflow-hidden', isDark ? 'border-slate-700' : 'border-slate-200')}>
                <div className="p-12 text-center text-slate-500">Loading...</div>
            </div>
        );
    }

    if (actions.length === 0) {
        return (
            <div className={clsx('rounded-xl border overflow-hidden', isDark ? 'border-slate-700' : 'border-slate-200')}>
                <div className="p-12 text-center">
                    <CheckSquare size={48} className="mx-auto text-slate-300 mb-4" />
                    <p className={clsx('text-lg font-medium', isDark ? 'text-white' : 'text-slate-800')}>
                        {allActionsCount === 0 ? 'No actions defined' : 'No matching actions'}
                    </p>
                    <p className="text-sm text-slate-500 mt-1">
                        {allActionsCount === 0
                            ? 'Add recommended actions from the PFMEA Editor for high-risk items.'
                            : 'Try adjusting your filters or search term.'}
                    </p>
                    {allActionsCount === 0 && hasPermission && (
                        <button
                            onClick={onEdit}
                            className="mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
                        >
                            Go to PFMEA Editor
                        </button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className={clsx('rounded-xl border overflow-hidden', isDark ? 'border-slate-700' : 'border-slate-200')}>
            <div className="divide-y divide-slate-200 dark:divide-slate-700">
                {actions.map(action => {
                    const expanded = expandedItems.has(action.id);
                    const overdue = isOverdue(action);

                    return (
                        <div
                            key={action.id}
                            className={clsx(
                                'transition-colors',
                                isDark ? 'bg-slate-900' : 'bg-white',
                                overdue && 'bg-red-50 dark:bg-red-900/20'
                            )}
                        >
                            {/* Main Row */}
                            <div
                                className="p-4 flex items-start gap-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800"
                                onClick={() => toggleExpand(action.id)}
                            >
                                <button className="mt-1 text-slate-400">
                                    {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                </button>

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className={clsx('px-2 py-0.5 rounded text-xs font-medium', getStatusColor(action.status))}>
                                            {action.status.replace('-', ' ')}
                                        </span>
                                        {overdue && (
                                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-500 text-white">
                                                OVERDUE
                                            </span>
                                        )}
                                        <span className={clsx(
                                            'px-2 py-0.5 rounded text-xs font-bold',
                                            action.pfmeaItem.actionPriority === 'H' ? 'bg-red-500 text-white' :
                                                action.pfmeaItem.actionPriority === 'M' ? 'bg-amber-500 text-white' :
                                                    'bg-green-500 text-white'
                                        )}>
                                            {action.pfmeaItem.actionPriority}
                                        </span>
                                    </div>
                                    <p className={clsx('font-medium', isDark ? 'text-white' : 'text-slate-800')}>
                                        {action.description}
                                    </p>
                                    <p className="text-sm text-slate-500 mt-1">
                                        <span className="font-mono">{action.pfmeaItem.processStepNumber}</span>
                                        {' · '}
                                        {action.pfmeaItem.failureMode}
                                    </p>
                                </div>

                                <div className="flex items-center gap-6 text-sm">
                                    {/* Owner */}
                                    <div className="flex items-center gap-1.5 text-slate-500">
                                        <User size={14} />
                                        {action.responsiblePerson || '—'}
                                    </div>

                                    {/* Due Date */}
                                    <div className={clsx(
                                        'flex items-center gap-1.5',
                                        overdue ? 'text-red-500 font-medium' : 'text-slate-500'
                                    )}>
                                        <Calendar size={14} />
                                        {formatDate(action.targetDate)}
                                    </div>

                                    {/* RPN Change */}
                                    {action.revisedRPN !== undefined && (
                                        <div className="flex items-center gap-1.5 text-green-600">
                                            <TrendingDown size={14} />
                                            <span>{action.originalRPN} → {action.revisedRPN}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Expanded Details */}
                            {expanded && (
                                <div className={clsx(
                                    'px-12 py-4 border-t',
                                    isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'
                                )}>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        {/* Source PFMEA */}
                                        <div>
                                            <label className="text-xs text-slate-500 uppercase">Process Step</label>
                                            <p className={isDark ? 'text-white' : 'text-slate-800'}>
                                                {action.pfmeaItem.processStepName}
                                            </p>
                                        </div>
                                        <div>
                                            <label className="text-xs text-slate-500 uppercase">Failure Mode</label>
                                            <p className={isDark ? 'text-white' : 'text-slate-800'}>
                                                {action.pfmeaItem.failureMode}
                                            </p>
                                        </div>
                                        <div>
                                            <label className="text-xs text-slate-500 uppercase">Original S×O×D</label>
                                            <p className={isDark ? 'text-white' : 'text-slate-800'}>
                                                {action.pfmeaItem.severity} × {action.pfmeaItem.occurrence} × {action.pfmeaItem.detection} = {action.originalRPN}
                                            </p>
                                        </div>
                                        {action.revisedRPN !== undefined ? (
                                            <div>
                                                <label className="text-xs text-slate-500 uppercase">Revised S×O×D</label>
                                                <p className="text-green-600 font-medium">
                                                    {action.revisedSeverity} × {action.revisedOccurrence} × {action.revisedDetection} = {action.revisedRPN}
                                                </p>
                                            </div>
                                        ) : (
                                            <div>
                                                <label className="text-xs text-slate-500 uppercase">Revised Ratings</label>
                                                <p className="text-amber-500">Not yet entered</p>
                                            </div>
                                        )}
                                    </div>

                                    {action.notes && (
                                        <div className="mt-4">
                                            <label className="text-xs text-slate-500 uppercase">Notes</label>
                                            <p className={isDark ? 'text-white' : 'text-slate-800'}>{action.notes}</p>
                                        </div>
                                    )}

                                    {/* Action Buttons */}
                                    <div className="mt-4 flex gap-2">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onEdit();
                                            }}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600"
                                        >
                                            <Edit3 size={14} />
                                            Edit in PFMEA
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ActionTrackerList;
